import React, { useEffect, useState } from "react";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./UPIMandate.css";

/* =====================================================
   CONSTANTS
===================================================== */

const SERVICE_NAME =
  "Forge Your Peak - Vision Ignite";

const SUBSCRIPTION_AMOUNT = 1; // ₹1 TESTING


/* =====================================================
   COMPONENT
===================================================== */

const UPIMandate = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("idle");
  const [statusMessage, setStatusMessage] = useState("");

  /* =====================================================
     LOAD RAZORPAY CHECKOUT
  ===================================================== */

  useEffect(() => {
    const loadRazorpay = () => {
      if (window.Razorpay) {
        return;
      }

      const existingScript = document.querySelector(
        'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
      );

      if (existingScript) {
        return;
      }

      const script = document.createElement("script");

      script.src =
        "https://checkout.razorpay.com/v1/checkout.js";

      script.async = true;

      script.onload = () => {
        console.log(
          "✅ Razorpay Checkout loaded"
        );
      };

      script.onerror = () => {
        console.error(
          "❌ Failed to load Razorpay Checkout"
        );
      };

      document.body.appendChild(script);
    };

    loadRazorpay();
  }, []);


  /* =====================================================
     INPUT HANDLERS
  ===================================================== */

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (status !== "idle") {
      setStatus("idle");
      setStatusMessage("");
    }
  };


  const handlePhoneChange = (phone) => {
    setFormData((previous) => ({
      ...previous,
      phone,
    }));

    if (status !== "idle") {
      setStatus("idle");
      setStatusMessage("");
    }
  };


  /* =====================================================
     VALIDATION
  ===================================================== */

  const validateForm = () => {
    const name = formData.name.trim();
    const email = formData.email.trim().toLowerCase();
    const phone = formData.phone.trim();

    if (!name) {
      return "Please enter your full name.";
    }

    if (name.length < 2) {
      return "Please enter a valid name.";
    }

    if (!email) {
      return "Please enter your email address.";
    }

    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      return "Please enter a valid email address.";
    }

    if (!phone) {
      return "Please enter your phone number.";
    }

    if (phone.length < 10) {
      return "Please enter a valid phone number.";
    }

    return null;
  };


  /* =====================================================
     SET ERROR
  ===================================================== */

  const showError = (message) => {
    setStatus("error");
    setStatusMessage(message);
  };


  /* =====================================================
     SET SUCCESS
  ===================================================== */

  const showSuccess = (message) => {
    setStatus("success");
    setStatusMessage(message);
  };


  /* =====================================================
     VERIFY UPI AUTOPAY
  ===================================================== */

  const verifyUPIAutopay = async ({
    paymentId,
    subscriptionId,
    signature,
  }) => {
    const response = await axios.post(
      "/api/razorpay/verify-upi-autopay",
      {
        razorpay_payment_id: paymentId,
        razorpay_subscription_id: subscriptionId,
        razorpay_signature: signature,
      }
    );

    if (!response.data?.success) {
      throw new Error(
        response.data?.message ||
          "UPI AutoPay verification failed."
      );
    }

    return response.data;
  };


  /* =====================================================
     OPEN RAZORPAY CHECKOUT
  ===================================================== */

  const openRazorpayCheckout = ({
    keyId,
    subscriptionId,
  }) => {
    if (!window.Razorpay) {
      throw new Error(
        "Razorpay Checkout is not available. Please refresh the page and try again."
      );
    }

    const options = {
      key: keyId,

      subscription_id: subscriptionId,

      name: "Forge Your Peak",

      description: SERVICE_NAME,

      prefill: {
        name: formData.name.trim(),

        email: formData.email
          .trim()
          .toLowerCase(),

        contact: formData.phone,
      },

      notes: {
        service: SERVICE_NAME,
        paymentMethod: "UPI_AUTOPAY",
      },

      theme: {
        color: "#20264F",
      },

      handler: async (paymentResponse) => {
        try {
          setStatus("processing");

          setStatusMessage(
            "Verifying your UPI AutoPay authorization..."
          );

          const subscriptionIdFromResponse =
            paymentResponse
              ?.razorpay_subscription_id ||
            subscriptionId;

          if (
            !paymentResponse?.razorpay_payment_id ||
            !subscriptionIdFromResponse ||
            !paymentResponse?.razorpay_signature
          ) {
            throw new Error(
              "Incomplete Razorpay verification details received."
            );
          }

          await verifyUPIAutopay({
            paymentId:
              paymentResponse.razorpay_payment_id,

            subscriptionId:
              subscriptionIdFromResponse,

            signature:
              paymentResponse.razorpay_signature,
          });

          showSuccess(
            "UPI AutoPay setup successful."
          );

          console.log(
            "✅ UPI AutoPay verification successful",
            {
              paymentId:
                paymentResponse.razorpay_payment_id,

              subscriptionId:
                subscriptionIdFromResponse,
            }
          );
        } catch (error) {
          console.error(
            "❌ UPI AutoPay verification failed:",
            error
          );

          showError(
            error.response?.data?.message ||
              error.message ||
              "UPI AutoPay verification failed. Please contact support."
          );
        } finally {
          setIsLoading(false);
        }
      },

      modal: {
        ondismiss: () => {
          setIsLoading(false);

          setStatus("idle");
          setStatusMessage("");
        },
      },
    };

    const razorpay =
      new window.Razorpay(options);

    razorpay.on(
      "payment.failed",
      (response) => {
        console.error(
          "❌ Razorpay UPI AutoPay failed:",
          response?.error
        );

        setIsLoading(false);

        showError(
          response?.error?.description ||
            "UPI AutoPay authorization failed. Please try again."
        );
      }
    );

    razorpay.open();
  };


  /* =====================================================
     CREATE UPI MANDATE
  ===================================================== */

  const handleSetupMandate = async () => {
    if (isLoading) {
      return;
    }

    try {
      setStatus("idle");
      setStatusMessage("");

      /* -----------------------------------------------
         VALIDATE FORM
      ------------------------------------------------ */

      const validationError =
        validateForm();

      if (validationError) {
        showError(validationError);
        return;
      }


      /* -----------------------------------------------
         CHECK RAZORPAY
      ------------------------------------------------ */

      if (!window.Razorpay) {
        showError(
          "Payment service is still loading. Please try again."
        );

        return;
      }


      setIsLoading(true);

      setStatus("processing");

      setStatusMessage(
        "Preparing your UPI AutoPay mandate..."
      );


      /* -----------------------------------------------
         CREATE MANDATE
      ------------------------------------------------ */

      const response = await axios.post(
        "/api/razorpay/create-upi-mandate",
        {
          name: formData.name.trim(),

          email: formData.email
            .trim()
            .toLowerCase(),

          phone: formData.phone,
        }
      );


      const data = response.data;


      if (!data?.success) {
        throw new Error(
          data?.message ||
            "Unable to create UPI AutoPay mandate."
        );
      }


      /* -----------------------------------------------
         RESPONSE DATA
      ------------------------------------------------ */

      const keyId =
        data?.data?.keyId;

      const subscription =
        data?.data?.subscription;


      if (!keyId) {
        throw new Error(
          "Razorpay Key ID was not received."
        );
      }


      if (!subscription?.id) {
        throw new Error(
          "Razorpay Subscription ID was not received."
        );
      }


      /* -----------------------------------------------
         OPEN RAZORPAY
      ------------------------------------------------ */

      openRazorpayCheckout({
        keyId,
        subscriptionId: subscription.id,
      });

    } catch (error) {
      console.error(
        "❌ UPI AutoPay initialization failed:",
        error
      );

      setIsLoading(false);

      showError(
        error.response?.data?.message ||
          error.message ||
          "Unable to start UPI AutoPay. Please try again."
      );
    }
  };


  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div className="mandate-page">

      {/* =================================================
          HEADER
      ================================================= */}

      <header className="mandate-header">

        <div className="brand">

          <div className="brand-mark">
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div className="brand-text">

            <h2>
              Forge Your Peak
            </h2>

            <p>
              VISION IGNITE
            </p>

          </div>

        </div>


        <nav className="desktop-nav">

          <a href="#home">
            Home
          </a>

          <a href="#how-it-works">
            How It Works
          </a>

          <a href="#faq">
            FAQ
          </a>

          <a href="#support">
            Support
          </a>

        </nav>

      </header>


      {/* =================================================
          MAIN
      ================================================= */}

      <main className="mandate-container">


        {/* =================================================
            LEFT SECTION
        ================================================= */}

        <section className="mandate-left">

          <div className="top-badge">

            <span>
              SIMPLE
            </span>

            <b>
              •
            </b>

            <span>
              SECURE
            </span>

            <b>
              •
            </b>

            <span>
              CANCEL ANYTIME
            </span>

          </div>


          <h1>
            Upgrade Your Vision.
            <br />
            Unlock Your Potential.
          </h1>


          <p className="hero-description">
            Join our performance program with
            a simple monthly plan powered by
            UPI AutoPay.
          </p>


          <div className="benefits">

            <div className="benefit">

              <div className="benefit-icon">
                ▣
              </div>

              <div>

                <h3>
                  Automatic Payments
                </h3>

                <p>
                  No manual payments every month
                </p>

              </div>

            </div>


            <div className="benefit">

              <div className="benefit-icon">
                ⌑
              </div>

              <div>

                <h3>
                  Secure & Trusted
                </h3>

                <p>
                  Powered by Razorpay UPI AutoPay
                </p>

              </div>

            </div>


            <div className="benefit">

              <div className="benefit-icon">
                ×
              </div>

              <div>

                <h3>
                  Cancel Anytime
                </h3>

                <p>
                  You're in control
                </p>

              </div>

            </div>


            <div className="benefit">

              <div className="benefit-icon">
                ▥
              </div>

              <div>

                <h3>
                  Focus on Your Progress
                </h3>

                <p>
                  We'll handle the payments
                </p>

              </div>

            </div>

          </div>


          <div className="quote-box">

            <span className="quote-mark">
              “
            </span>

            <div>

              <p>
                Better vision. Sharper focus.
                A stronger you.
              </p>

              <span>
                – Forge Your Peak
              </span>

            </div>

          </div>

        </section>


        {/* =================================================
            RIGHT PAYMENT CARD
        ================================================= */}

        <section className="payment-card">

          <h2>
            Complete Your Subscription
          </h2>


          <p className="payment-subtitle">
            Set up UPI AutoPay and get started
            in seconds.
          </p>


          {/* =================================================
              BILLING INFORMATION
          ================================================= */}

          <div className="billing-section">

            <h3>
              Billing Information
            </h3>


            <input
              type="text"
              name="name"
              placeholder="Full name"
              value={formData.name}
              onChange={handleChange}
              disabled={isLoading}
              autoComplete="name"
            />


            <input
              type="email"
              name="email"
              placeholder="Email address"
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading}
              autoComplete="email"
            />


            <div className="phone-input-wrapper">

              <PhoneInput
                country="in"
                value={formData.phone}
                onChange={handlePhoneChange}
                enableSearch={true}
                countryCodeEditable={false}
                placeholder="Phone number"
                inputProps={{
                  name: "phone",
                  required: true,
                  autoComplete: "tel",
                }}
                disabled={isLoading}
              />

            </div>

          </div>


          {/* =================================================
              AMOUNT
          ================================================= */}

          <div className="amount-box">

            <div className="amount">

              ₹{SUBSCRIPTION_AMOUNT}

              <span>
                / Month
              </span>

            </div>

            <p>
              Testing Amount
            </p>

          </div>


          {/* =================================================
              UPI AUTOPAY INFORMATION
          ================================================= */}

          <div className="autopay-info">

            <div className="autopay-icon">
              ⚡
            </div>

            <div>

              <h3>
                UPI AutoPay Mandate
              </h3>

              <p>
                You will be redirected to your
                UPI app to approve the mandate.
              </p>

            </div>

          </div>


          {/* =================================================
              STATUS
          ================================================= */}

          {status === "error" && (
            <div
              className="payment-error"
              role="alert"
            >
              {statusMessage}
            </div>
          )}


          {status === "success" && (
            <div
              className="payment-success"
              role="status"
            >
              {statusMessage}
            </div>
          )}


          {status === "processing" && (
            <div
              className="payment-processing"
              role="status"
            >
              {statusMessage}
            </div>
          )}


          {/* =================================================
              CTA
          ================================================= */}

          <button
            type="button"
            className="setup-button"
            onClick={handleSetupMandate}
            disabled={isLoading}
            aria-busy={isLoading}
          >

            <span>
              {isLoading
                ? "Processing..."
                : "Setup UPI AutoPay"}
            </span>

            <span className="arrow">
              →
            </span>

          </button>


          {/* =================================================
              POWERED BY
          ================================================= */}

          <div className="powered-section">

            <div className="powered-line"></div>

            <span>
              Powered by
            </span>

            <div className="powered-line"></div>

          </div>


          {/* =================================================
              PAYMENT PROVIDERS
          ================================================= */}

          <div className="payment-providers">

            <strong className="razorpay-text">
              Razorpay
            </strong>

            <strong className="upi-text">
              UPI
            </strong>

            <span className="upi-app gpay">
              G
            </span>

            <span className="upi-app phonepe">
              पे
            </span>

            <span className="upi-app paytm">
              Paytm
            </span>

          </div>


          {/* =================================================
              SECURITY
          ================================================= */}

          <div className="secure-payment">

            <span>
              🔒
            </span>

            <span>
              100% Secure Payments
            </span>

          </div>

        </section>

      </main>

    </div>
  );
};


export default UPIMandate;